import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, PhoneCall, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { getSiteSettings } from '../../services/content.service';
import SmartLink from '../common/SmartLink';
import { useSiteContent } from '../../content/useSiteContent';

export default function Topbar() {
  const defaultAnnouncements = [
    "Buy 1 Get 1 Free on All Eyewear",
    "Shop online, click and collect from 260+ stores",
    "Free eye testing available at all Rayoptics clinics",
    "Get 20% off on premium contact lenses this week",
    "New arrivals: Explore our latest summer sunglass collection"
  ];

  // Editable in the dashboard (Site Settings → Announcement bar): one message per line, rotated in order.
  const { data: settings } = useQuery({ queryKey: ['site-settings'], queryFn: getSiteSettings, staleTime: 60_000, retry: false });
  const managed = settings ? String(settings.announcement_text || '').split('\n').map((l) => l.trim()).filter(Boolean) : [];
  const dashboardHidden = !!settings && (settings.announcement_enabled === false || managed.length === 0);
  const announcements = managed.length ? managed : defaultAnnouncements;
  const announcementLink = settings?.announcement_link || '';

  const { site } = useSiteContent();
  const [isVisible, setIsVisible] = useState(true);
  const [[currentIndex, direction], setPage] = useState([0, 1]);

  useEffect(() => {
    if (!isVisible) return;
    const timer = setInterval(() => {
      setPage(([prevIndex]) => [(prevIndex + 1) % announcements.length, 1]);
    }, 4000);
    return () => clearInterval(timer);
  }, [announcements.length, isVisible]);

  const prevSlide = () => {
    setPage(([prevIndex]) => [(prevIndex - 1 + announcements.length) % announcements.length, -1]);
  };

  const nextSlide = () => {
    setPage(([prevIndex]) => [(prevIndex + 1) % announcements.length, 1]);
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 30 : -30,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 30 : -30,
      opacity: 0
    })
  };

  if (!isVisible || dashboardHidden) return null;

  return (
    <div className="bg-[#121212] text-white w-full font-montserrat relative z-50">
      <div className="relative max-w-7xl mx-auto py-1 pl-3 sm:pl-6 lg:pl-8 pr-2.5 sm:pr-3 flex items-center justify-between gap-1 sm:gap-2 overflow-hidden">

        {/* Animated Announcement — fills the space on small screens; pinned to the true center of the bar on xl */}
        <div className="flex-1 flex justify-center items-center gap-1 min-w-0 xl:absolute xl:inset-x-0 xl:mx-auto xl:w-max xl:flex-none">
          <button
            onClick={prevSlide}
            className="shrink-0 hover:text-white/70 p-1 hidden sm:block"
            aria-label="Previous announcement"
          >
            <ChevronLeft size={14} />
          </button>

          <div className="relative overflow-hidden h-5 w-full max-w-[260px] xs:max-w-[340px] sm:max-w-[420px] xl:w-[420px]">
            <AnimatePresence custom={direction} initial={false}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
                className="absolute w-full text-center text-[11px] sm:text-[12px] font-semibold tracking-wide px-1 truncate"
              >
                {announcementLink ? (
                  <SmartLink to={announcementLink} className="hover:underline">
                    {announcements[currentIndex % announcements.length]}
                  </SmartLink>
                ) : (
                  announcements[currentIndex % announcements.length]
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <button
            onClick={nextSlide}
            className="shrink-0 hover:text-white/70 p-1 hidden sm:block"
            aria-label="Next announcement"
          >
            <ChevronRight size={14} />
          </button>
        </div>

        {/* Right links */}
        <div className="flex items-center gap-3 sm:gap-6 text-[11px] font-normal text-white/75 shrink-0 ml-auto relative z-10">
          <SmartLink to={site.header.topbar_link.href} className="flex items-center gap-1.5 hover:text-white transition-colors">
            <PhoneCall size={14} strokeWidth={1.75} />
            <span className="hidden sm:inline">{site.header.topbar_link.label}</span>
          </SmartLink>

          {/* Close */}
          <button
            onClick={() => setIsVisible(false)}
            className="p-1 -mr-1 text-white/75 hover:text-white transition-colors"
            aria-label="Close announcement bar"
          >
            <X size={11} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
